import { createContext, useContext } from "react";

import type { components } from "@/core/api/schema";

/**
 * The session's shape and context, kept apart from the provider on purpose: this file has no other imports,
 * so a dev-server hot reload of the provider's dependencies never creates a second context (which made
 * useSession throw until a full reload).
 */
export type Me = components["schemas"]["Me"];

/** `unavailable`: the API couldn't be reached to check the session (not the same as signed out). */
export type SessionStatus = "loading" | "signed-out" | "signed-in" | "unavailable";

export interface SessionState {
  status: SessionStatus;
  me: Me | null;
  /** Checks the session again (after `unavailable`). */
  retry(): void;
  signInWithGoogle(): Promise<void>;
  signInWithEmail(email: string, password: string): Promise<void>;
  /** A new email account (someone invited with an address that isn't a Google account). */
  createAccount(email: string, password: string): Promise<void>;
  sendPasswordReset(email: string): Promise<void>;
  /**
   * Signs in again as the same person, for a change that needs a fresh sign-in (the API answers
   * RECENT_SIGN_IN_REQUIRED). Keeps every screen's data. False if a different account signed in: that
   * becomes an ordinary switch of account and the change must not be retried.
   */
  confirmWithGoogle(): Promise<boolean>;
  confirmWithEmail(email: string, password: string): Promise<boolean>;
  /** Ends this portal's session in this browser; `everywhere` also ends every other session of the person. */
  signOut(options?: { everywhere?: boolean }): Promise<void>;
}

export const SessionContext = createContext<SessionState | null>(null);

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside SessionProvider");
  return value;
}
